import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import './StoreBoxPage.css';

// Pack a carton for the Charleston store. Pick the King St warehouse PO, add
// what's going in this box (type a quantity, tap + case, or scan the item /
// case label), then print a QR label for the outside. The store scans that one
// QR on its Receive Warehouse Order page and every item in the box is counted.
// See apps/run/server/storeBoxRoutes.js for the label format.

const packedKey = (poId) => `store-box-packed-${poId}`;

function loadPacked(poId) {
  try { return JSON.parse(localStorage.getItem(packedKey(poId)) || '{}'); } catch { return {}; }
}
function savePacked(poId, packed) {
  try { localStorage.setItem(packedKey(poId), JSON.stringify(packed)); } catch { /* memory only */ }
}

// A scan is the case label (QR /ops/<id>, barcode = case internal id), the
// item's own id/SKU, or a product UPC.
function matchScan(items, raw) {
  let v = String(raw || '').trim();
  const ops = v.match(/\/ops\/([^/?#\s]+)/i);
  if (ops) v = decodeURIComponent(ops[1]);
  v = v.toUpperCase();
  const keys = [v];
  if (/^\d+$/.test(v)) keys.push(v.replace(/^0+/, ''));
  for (const it of items) {
    const caseKeys = [it.case_sku, it.case_item_id].filter(Boolean).map((k) => String(k).toUpperCase());
    if (caseKeys.some((k) => keys.includes(k))) return { item: it, qty: it.case_qty };
    const unitKeys = [it.sku, it.item_id, it.upc, it.upc ? String(it.upc).replace(/^0+/, '') : null]
      .filter(Boolean).map((k) => String(k).toUpperCase());
    if (unitKeys.some((k) => keys.includes(k))) return { item: it, qty: 1 };
  }
  return null;
}

// Handheld scanners type fast and end with Enter; people type slowly.
function useScanner(onScan, enabled) {
  const ref = useRef(onScan);
  useEffect(() => { ref.current = onScan; }, [onScan]);
  useEffect(() => {
    if (!enabled) return undefined;
    let buf = '';
    let first = 0;
    let last = 0;
    const onKey = (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const now = performance.now();
      if (e.key === 'Enter') {
        if (buf.length >= 3 && (now - first) / buf.length < 60) {
          e.preventDefault();
          e.stopPropagation();
          ref.current(buf);
        }
        buf = '';
        return;
      }
      if (e.key.length !== 1) return;
      if (now - last > 200) { buf = ''; first = now; }
      buf += e.key;
      last = now;
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [enabled]);
}

function downloadZpl(zpl, boxId) {
  const blob = new Blob([zpl], { type: 'application/octet-stream' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `store-box-${boxId}.zpl`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Same path as the shipment labels: Zebra via the server, .zpl download if the
// printer can't be reached.
async function printZpl(zpl, boxId) {
  try {
    const printerIp = localStorage.getItem('zebraPrinterIp') || '192.168.1.100';
    await axios.post('/api/print/zpl', { zpl, printerIp });
    return 'printed';
  } catch {
    downloadZpl(zpl, boxId);
    return 'downloaded';
  }
}

function formatDate(d) {
  if (!d) return '';
  const dt = new Date(d);
  return isNaN(dt) ? d : dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function StoreBoxPage() {
  const [orders, setOrders] = useState(null);
  const [error, setError] = useState(null);
  const [poId, setPoId] = useState(null);
  const [box, setBox] = useState({}); // item_id -> qty in this carton
  const [packed, setPacked] = useState({}); // item_id -> qty already labeled on this PO (this PC)
  const [message, setMessage] = useState(null);
  const [busy, setBusy] = useState(false);
  const [lastLabel, setLastLabel] = useState(null);
  const [filter, setFilter] = useState('');

  const load = useCallback(async () => {
    setError(null);
    try {
      const { data } = await axios.get('/api/store-boxes/pos');
      setOrders(data.orders || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
      setOrders([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const po = useMemo(() => (orders || []).find((o) => o.id === poId) || null, [orders, poId]);

  const openPo = (id) => {
    setPoId(id);
    setBox({});
    setPacked(loadPacked(id));
    setMessage(null);
    setLastLabel(null);
    setFilter('');
  };

  const left = (it) => Math.max(0, it.remaining - (packed[it.item_id] || 0));

  const setQty = (it, q) => {
    setBox((prev) => {
      const next = { ...prev };
      const v = Math.max(0, Math.floor(Number(q) || 0));
      if (v) next[it.item_id] = v; else delete next[it.item_id];
      return next;
    });
  };

  const add = (it, n) => setQty(it, (box[it.item_id] || 0) + n);

  const onScan = (raw) => {
    if (!po || busy) return;
    const hit = matchScan(po.items, raw);
    if (!hit) {
      setMessage({ kind: 'error', text: `${raw} isn't open on ${po.tranid}.` });
      return;
    }
    add(hit.item, hit.qty);
    setMessage({ kind: 'ok', text: `+${hit.qty} ${hit.item.name}` });
  };
  useScanner(onScan, !!po);

  const boxLines = po ? po.items.filter((it) => box[it.item_id] > 0) : [];
  const boxUnits = boxLines.reduce((s, it) => s + box[it.item_id], 0);
  const over = boxLines.filter((it) => box[it.item_id] > left(it));

  const shown = useMemo(() => {
    if (!po) return [];
    const t = filter.trim().toUpperCase();
    if (!t) return po.items;
    return po.items.filter((it) => [it.sku, it.name, it.case_sku].some((v) => (v || '').toUpperCase().includes(t)));
  }, [po, filter]);

  const printBox = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const { data } = await axios.post('/api/store-boxes/label', {
        po_id: po.id,
        lines: boxLines.map((it) => ({ item_id: it.item_id, qty: box[it.item_id] })),
      });
      const how = await printZpl(data.zpl, data.box_id);
      const nextPacked = { ...packed };
      for (const l of data.lines) nextPacked[l.item_id] = (nextPacked[l.item_id] || 0) + l.qty;
      setPacked(nextPacked);
      savePacked(po.id, nextPacked);
      setLastLabel(data);
      setBox({});
      setMessage({
        kind: 'ok',
        text: how === 'printed'
          ? `Printed box ${data.box_id} (${boxUnits} units). Stick it on the outside of the carton.`
          : `Printer not reachable, so box ${data.box_id} downloaded as a .zpl file. Send it to the Zebra to print.`,
      });
    } catch (e) {
      setMessage({ kind: 'error', text: e.response?.data?.error || e.message });
    } finally {
      setBusy(false);
    }
  };

  const reprint = async () => {
    if (!lastLabel) return;
    const how = await printZpl(lastLabel.zpl, lastLabel.box_id);
    setMessage({ kind: 'ok', text: how === 'printed' ? `Reprinted box ${lastLabel.box_id}.` : `Downloaded box ${lastLabel.box_id} as a .zpl file.` });
  };

  if (!po) {
    return (
      <div className="store-box-page">
        <div className="store-box-header">
          <div>
            <h1>Pack Store Box</h1>
            <p className="muted">For loose cartons going to King St. Pick the PO, add what's in the box, print the QR label for the outside. The store scans it once to check everything in.</p>
          </div>
        </div>
        {error && <div className="store-box-msg error">{error}</div>}
        {orders === null && <p className="muted">Loading King St POs from NetSuite...</p>}
        {orders && orders.length === 0 && !error && <p className="muted">No open King St warehouse POs.</p>}
        <div className="store-box-list">
          {(orders || []).map((o) => {
            const units = o.items.reduce((s, it) => s + it.remaining, 0);
            return (
              <button key={o.id} className="store-box-po" onClick={() => openPo(o.id)}>
                <div>
                  <strong>{o.tranid}</strong> <span className="muted">{o.memo}</span>
                </div>
                <div className="muted">{formatDate(o.date)} &middot; {o.items.length} items, {units} units open</div>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="store-box-page">
      <button className="store-box-back" onClick={() => setPoId(null)}>&larr; All POs</button>
      <div className="store-box-header">
        <div>
          <h1>{po.tranid}</h1>
          <p className="muted">{po.memo} &middot; {formatDate(po.date)}</p>
        </div>
        <div className="store-box-total">
          <div className="big">{boxUnits}</div>
          <div className="muted">units in this box</div>
        </div>
      </div>

      <div className={`store-box-msg ${message?.kind || ''}`}>
        {message ? message.text : 'Scan an item or case label, or type a quantity. Then print the box label.'}
      </div>

      <input
        className="store-box-filter"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        placeholder="Find an item (name or SKU)"
      />

      <div className="store-box-items">
        {shown.map((it) => {
          const q = box[it.item_id] || 0;
          const already = packed[it.item_id] || 0;
          return (
            <div key={it.item_id} className={`store-box-item ${q ? 'in-box' : ''}`}>
              <div className="info">
                <div className="name">{it.name}</div>
                <div className="muted">
                  {it.sku} &middot; {left(it)} left to send{already ? ` (${already} already boxed)` : ''}
                </div>
              </div>
              <input
                type="number"
                min="0"
                inputMode="numeric"
                value={q || ''}
                placeholder="0"
                onChange={(e) => setQty(it, e.target.value)}
                className={q > left(it) ? 'over' : ''}
              />
              <button className="secondary" onClick={() => add(it, it.case_qty)} disabled={busy}>
                + {it.case_qty === 1 ? '1' : `case (${it.case_qty})`}
              </button>
            </div>
          );
        })}
      </div>

      <div className="store-box-actions">
        {over.length > 0 && (
          <p className="warn">
            More than the PO still needs: {over.map((it) => `${it.name} (${box[it.item_id]} of ${left(it)})`).join(', ')}. The store can only check in what's open on the PO.
          </p>
        )}
        <button className="primary" onClick={printBox} disabled={!boxUnits || busy}>
          {busy ? 'Printing...' : `Print box label (${boxUnits} units)`}
        </button>
        {boxUnits > 0 && <button className="secondary" onClick={() => setBox({})} disabled={busy}>Clear box</button>}
        {lastLabel && <button className="secondary" onClick={reprint} disabled={busy}>Reprint box {lastLabel.box_id}</button>}
      </div>
    </div>
  );
}
